import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Combobox, Option, Field, TagGroup, Tag, Avatar, Spinner, Tooltip } from '@fluentui/react-components';
import { DocumentPdf24Regular, Image24Regular, Document24Regular, FolderZip24Regular, Table24Regular } from '@fluentui/react-icons';
import { Props, mapStateToProps, mapDispatchToProps } from './FileSelector.Functions';
import { FileUpload } from '../../../../models/Entities/Contracts/FileUpload';

// styles 
import './FileSelector.Styles.css';

const iconForExtension = (ext: string) => {
  switch (ext) {
    case 'pdf':
      return <DocumentPdf24Regular color="var(--ichtys-error)"/>;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
      return <Image24Regular/>;
    case 'zip':
    case 'rar':
      return <FolderZip24Regular/>;
    case 'xlsx':
    case 'xls':
      return <Table24Regular color="var(--ichtys-success)"/>;
    case 'json':
    case 'xml':
    case 'txt':
      return <Document24Regular/>;
    case 'csv':
      return <Table24Regular color="var(--ichtys-success)"/>;
    case 'js':
    case 'ts':
    case 'py':
    case 'java':
      return <Document24Regular/>;
    default:
      return <Document24Regular/>;
  }
};

const FileSelectorComponent: React.FC<Props> = ({ contractId, multiple = false, files, isLoading, GetAllFileUploads, onChange }) => {
  const [selected, setSelected] = useState<FileUpload[]>([]);
  const [comboValue, setComboValue] = useState<string>('');

  useEffect(() => {
    if (contractId) {
      GetAllFileUploads({ entityId: contractId, entityType: 'contract' });
    }
  }, [contractId]);

  const options = useMemo(() => {
    const selectedIds = new Set(selected.map(s => s.id));
    const available = files.filter(f => !selectedIds.has(f.id));
    return available.map(f => ({ key: String(f.id), label: f.fileName ?? f.fileId, file: f }));
  }, [files, selected]);

  const onSelect = (file: FileUpload) => {
    const exists = selected.find(s => s.id === file.id);
    let next: FileUpload[];
    if (multiple) {
      next = exists ? selected.filter(s => s.id !== file.id) : [...selected, file];
    } else {
      next = exists ? [] : [file];
    }
    setSelected(next);
    onChange && onChange(next);
    setComboValue('');
  };

  const onRemove = (id: string) => {
    const numeric = Number(id);
    const next = selected.filter(s => s.id !== numeric);
    setSelected(next);
    onChange && onChange(next);
  };

  const { t } = useTranslation();

  // The name alone left no way to tell two similar uploads apart; size and
  // extension are what actually disambiguate them.
  const formatFileSize = (bytes?: number): string => {
    if (!bytes || bytes <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
      value = value / 1024;
      unit++;
    }
    return (unit === 0 ? value : Number(value.toFixed(1))) + ' ' + units[unit];
  };

  return (

    <div style={{ display: 'grid', gap: 8 }}>
      <Field>
        {isLoading ? (
          <Spinner size='small' />
        ) : (
          <Combobox
            placeholder={t('contractIAImport.form.fileSelectorPlaceholder')}
            value={comboValue}
            onChange={(ev) => setComboValue((ev.target as HTMLInputElement).value)}
            selectedOptions={[]}
            onOptionSelect={(_ev, data) => {
              const found = options.find(o => o.key === (data.optionValue ?? ''));
              if (found) onSelect(found.file);
            }}
          >
            {options.map(opt => (
              <Option key={opt.key} text={opt.label} value={opt.key}>
                <div className='OptionContent'>
                  <span className='IconWrap'>{iconForExtension(opt.file.fileExtension)}</span>
                  <span className='Clamp2'>{opt.label}</span>
                </div>
              </Option>
            ))}
          </Combobox>
        )}
      </Field>

      {selected.length > 0 && (
        <TagGroup onDismiss={(_, data) => onRemove(String(data.value))} className='VerticalTagGroup'>
          {selected.map(s => (
            <Tooltip key={s.id} content={s.fileName} relationship="label">
              <Tag value={String(s.id)} dismissible className='TagGroupFileList' style={{ width: '100%' }}>
                <div className='TagRow'>
                  <Avatar icon={iconForExtension(s.fileExtension)} shape='square' />
                  <span className='FileRowText'>
                    <span className='FileRowName'>{s.fileName}</span>
                    <span className='FileRowMeta'>
                      {s.fileExtension && (
                        <span className='FileRowExt'>{s.fileExtension.replace('.', '').toUpperCase()}</span>
                      )}
                      {formatFileSize(s.fileSize)}
                    </span>
                  </span>
                </div>
              </Tag>
            </Tooltip>
          ))}
        </TagGroup>
      )}
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(FileSelectorComponent);
